import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { uploadBranchLogo, deleteBranchLogo } from '../../services/branchService'
import { apiError } from '../../api/apiError'
import type { BranchResponse } from '../../types'
import './BranchLogoCard.css'

const MAX_BYTES = 1024 * 1024
const ACCEPT = 'image/png,image/jpeg,image/webp'

interface Props {
  branch: BranchResponse
  readOnly: boolean
  onUpdated: (b: BranchResponse) => void
  onToast: (msg: string, type: 'success' | 'error') => void
}

export default function BranchLogoCard({ branch, readOnly, onUpdated, onToast }: Props) {
  const { t } = useTranslation()
  const inputRef = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(false)

  async function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    if (file.size > MAX_BYTES) {
      onToast(t('admin.branch_logo.too_large'), 'error')
      return
    }
    setBusy(true)
    try {
      onUpdated(await uploadBranchLogo(branch.id, file))
      onToast(t('admin.branch_logo.uploaded'), 'success')
    } catch (err) {
      const { code, message } = apiError(err)
      const errorMap: Record<string, string> = {
        LOGO_TOO_LARGE: t('admin.branch_logo.too_large'),
        LOGO_UNSUPPORTED_TYPE: t('admin.branch_logo.unsupported_type'),
      }
      onToast((code && errorMap[code]) || message || t('admin.branch_logo.upload_failed'), 'error')
    } finally {
      setBusy(false)
    }
  }

  async function handleDelete() {
    if (!confirm(t('admin.branch_logo.delete_confirm'))) return
    setBusy(true)
    try {
      await deleteBranchLogo(branch.id)
      onUpdated({ ...branch, logoUrl: null })
      onToast(t('admin.branch_logo.deleted'), 'success')
    } catch (err) {
      onToast(apiError(err).message || t('admin.branch_logo.delete_failed'), 'error')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="admin-detail-card branch-logo">
      <p className="admin-detail-card__title">{t('admin.branch_logo.card_title')}</p>

      <div className="branch-logo__body">
        <div className="branch-logo__preview">
          {branch.logoUrl
            ? <img src={branch.logoUrl} alt={branch.displayName || branch.name} />
            : <span className="branch-logo__empty">{t('admin.branch_logo.empty')}</span>}
        </div>
        <p className="branch-logo__hint">{t('admin.branch_logo.hint')}</p>
      </div>

      {!readOnly && (
        <div className="branch-logo__actions">
          <input ref={inputRef} type="file" accept={ACCEPT} hidden onChange={handleFile} />
          <button className="admin-btn admin-btn--primary" disabled={busy} onClick={() => inputRef.current?.click()}>
            {busy ? t('admin.branch_logo.uploading') : branch.logoUrl ? t('admin.branch_logo.replace') : t('admin.branch_logo.upload')}
          </button>
          {branch.logoUrl && (
            <button className="admin-btn admin-btn--danger" disabled={busy} onClick={handleDelete}>
              {t('admin.branch_logo.delete')}
            </button>
          )}
        </div>
      )}
    </div>
  )
}
